'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  PaginationState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { Check, ChevronDown, Download, Search, SlidersHorizontal, X } from 'lucide-react';
import type * as XLSXType from 'xlsx';
import { cn } from '@/lib/utils';
import { apiFetch } from '@/lib/api';
import { tryBuildCompanySlugFromCanonical } from '@/lib/company-utils';
import { useLocale } from '@/hooks/use-locale';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Card, CardFooter, CardTable } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LocaleLink } from '@/components/locale-link';
import {
  DataGrid,
  DataGridApiFetchParams,
} from '@/components/ui/data-grid';
import { DataGridColumnHeader } from '@/components/ui/data-grid-column-header';
import { DataGridColumnVisibility } from '@/components/ui/data-grid-column-visibility';
import { DataGridPagination } from '@/components/ui/data-grid-pagination';
import { DataGridTable } from '@/components/ui/data-grid-table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

export type TendersResponse = {
  data: TenderRow[];
  pagination: { page: number; limit: number; total: number };
};

type TenderRow = {
  id: number;
  publication_number: string;
  publication_date: string | null;
  title: string | null;
  buyer_name: string | null;
  buyer_town: string | null;
  buyer_country_code: string | null;
  bundesland: string | null;
  contract_type: string | null;
  procedure_type: string | null;
  estimated_value: number | null;
  value_currency: string | null;
  cpv_codes: string[] | null;
  cpv_descriptions: (string | null)[] | null;
  nuts_codes: string[] | null;
  submission_deadline: string | null;
  document_type: string | null;
  winner_name: string | null;
  awarded_value: number | null;
  awarded_currency: string | null;
  highest_tender_value: number | null;
  lowest_tender_value: number | null;
  buyer_street: string | null;
  buyer_postal_code: string | null;
  buyer_email: string | null;
  buyer_phone: string | null;
  buyer_company_id: string | null;
  buyer_company_size: string | null;
  buyer_eu_id: string | null;
  buyer_canonical_register_court: string | null;
  buyer_canonical_register_type: string | null;
  buyer_canonical_register_id: string | null;
  buyer_canonical_sitz: string | null;
  buyer_canonical_name: string | null;
  winner_town: string | null;
  winner_street: string | null;
  winner_postal_code: string | null;
  winner_url: string | null;
  winner_email: string | null;
  winner_phone: string | null;
  winner_company_id: string | null;
  winner_company_size: string | null;
  winner_eu_id: string | null;
  winner_canonical_register_court: string | null;
  winner_canonical_register_type: string | null;
  winner_canonical_register_id: string | null;
  winner_canonical_sitz: string | null;
  winner_canonical_name: string | null;
  external_reference_url: string | null;
  xml_format: string | null;
  parse_error: string | null;
};

function formatCurrency(value: number | null, currency: string | null): string {
  if (value == null) return '\u2014';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currency || 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  try {
    return new Date(dateStr).toLocaleDateString('de-DE');
  } catch {
    return dateStr;
  }
}

const VALUE_RANGES: Record<string, { min?: string; max?: string }> = {
  '0-100k': { max: '100000' },
  '100k-1m': { min: '100000', max: '1000000' },
  '1m-10m': { min: '1000000', max: '10000000' },
  '10m-50m': { min: '10000000', max: '50000000' },
  '50m+': { min: '50000000' },
};

function valueRangeToParams(range: string): Record<string, string> {
  const r = VALUE_RANGES[range];
  if (!r) return {};
  const params: Record<string, string> = {};
  if (r.min) params.value_min = r.min;
  if (r.max) params.value_max = r.max;
  return params;
}

const contractTypeBadge: Record<string, string> = {
  works: 'destructive',
  supplies: 'warning',
  services: 'info',
};

const TendersList = ({
  isGuest,
  canExport = false,
  onSignIn,
  initialData,
}: {
  isGuest?: boolean;
  /** Whether the user may export (Pro/Admin). The button shows for all
   *  authenticated users; non-entitled users get a "Pro feature" prompt. */
  canExport?: boolean;
  onSignIn?: () => void;
  initialData?: TendersResponse | null;
}) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: isGuest ? 20 : 50,
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'publication_date', desc: true },
  ]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContractType, setSelectedContractType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedBundesland, setSelectedBundesland] = useState('all');
  const [selectedValueRange, setSelectedValueRange] = useState('all');
  const [selectedCpvCode, setSelectedCpvCode] = useState('');
  const [cpvCodesOpen, setCpvCodesOpen] = useState(false);
  const [cpvSearch, setCpvSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [cpvCodesList, setCpvCodesList] = useState<{ code: string; description: string }[]>([]);
  const cpvListRef = useRef<HTMLDivElement>(null);
  const cpvDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load top-level CPV codes on mount, then narrow on search
  useEffect(() => {
    apiFetch('/api/search/cpv-codes')
      .then((res) => res.json())
      .then((data) => { if (Array.isArray(data)) setCpvCodesList(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (cpvDebounceRef.current) clearTimeout(cpvDebounceRef.current);
    if (cpvSearch.length >= 2) {
      cpvDebounceRef.current = setTimeout(() => {
        apiFetch(`/api/search/cpv-codes?query=${encodeURIComponent(cpvSearch)}`)
          .then((res) => res.json())
          .then((data) => { if (Array.isArray(data)) setCpvCodesList(data); })
          .catch(() => {});
      }, 200);
    } else if (cpvSearch.length === 0) {
      apiFetch('/api/search/cpv-codes')
        .then((res) => res.json())
        .then((data) => { if (Array.isArray(data)) setCpvCodesList(data); })
        .catch(() => {});
    }
    return () => { if (cpvDebounceRef.current) clearTimeout(cpvDebounceRef.current); };
  }, [cpvSearch]);

  const fetchTenders = async ({
    pageIndex,
    pageSize,
    sorting,
    searchQuery,
  }: DataGridApiFetchParams) => {
    const sortField = sorting?.[0]?.id || '';
    const sortDirection = sorting?.[0]?.desc ? 'desc' : 'asc';

    const params = new URLSearchParams({
      page: String(pageIndex + 1),
      limit: String(pageSize),
      ...(sortField ? { sort: sortField, dir: sortDirection } : {}),
      ...(searchQuery ? { query: searchQuery } : {}),
      ...(selectedContractType !== 'all'
        ? { contract_type: selectedContractType }
        : {}),
      ...(selectedStatus !== 'all' ? { status: selectedStatus } : {}),
      ...(selectedBundesland !== 'all' ? { bundesland: selectedBundesland } : {}),
      ...(selectedValueRange !== 'all' ? valueRangeToParams(selectedValueRange) : {}),
      ...(selectedCpvCode ? { cpv_code: selectedCpvCode } : {}),
    });

    const response = await apiFetch(
      `/api/tenders?${params.toString()}`,
    );

    if (!response.ok) {
      throw new Error('Failed to fetch tenders');
    }

    return response.json();
  };

  // Build the active-filter query params (without pagination) shared by the
  // grid fetch and the export.
  const buildFilterParams = (): Record<string, string> => ({
    ...(searchQuery ? { query: searchQuery } : {}),
    ...(selectedContractType !== 'all'
      ? { contract_type: selectedContractType }
      : {}),
    ...(selectedStatus !== 'all' ? { status: selectedStatus } : {}),
    ...(selectedBundesland !== 'all' ? { bundesland: selectedBundesland } : {}),
    ...(selectedValueRange !== 'all'
      ? valueRangeToParams(selectedValueRange)
      : {}),
    ...(selectedCpvCode ? { cpv_code: selectedCpvCode } : {}),
  });

  // Excel export — re-fetches the current result set (same filters + sort) up
  // to EXPORT_MAX rows and writes an .xlsx, mirroring the /search export.
  // Export is Pro/Admin only; non-entitled users get a "Pro feature" prompt.
  const [isExporting, setIsExporting] = useState(false);
  const [showProDialog, setShowProDialog] = useState(false);
  const EXPORT_MAX = 5000;
  const handleExport = async () => {
    if (!canExport) {
      setShowProDialog(true);
      return;
    }
    if (isExporting) return;
    setIsExporting(true);
    try {
      const sortField = sorting?.[0]?.id || '';
      const sortDirection = sorting?.[0]?.desc ? 'desc' : 'asc';
      const params = new URLSearchParams({
        page: '1',
        limit: String(EXPORT_MAX),
        ...(sortField ? { sort: sortField, dir: sortDirection } : {}),
        ...buildFilterParams(),
      });
      const response = await apiFetch(`/api/tenders?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to export tenders');
      const json: TendersResponse = await response.json();
      const XLSX: typeof XLSXType = await import('xlsx');
      const rows = (json.data || []).map((r, i) => ({
        '#': i + 1,
        [t('tenders.columns.pubNr', 'Pub. Nr.')]: r.publication_number,
        [t('tenders.columns.date', 'Date')]: r.publication_date ?? '',
        [t('tenders.columns.title', 'Title')]: r.title ?? '',
        [t('tenders.columns.buyer', 'Buyer')]: r.buyer_name ?? '',
        [t('tenders.columns.town', 'Town')]: r.buyer_town ?? '',
        [t('tenders.bundesland', 'Bundesland')]: r.bundesland ?? '',
        [t('tenders.columns.type', 'Type')]: r.contract_type ?? '',
        [t('tenders.columns.estValue', 'Est. Value')]: r.estimated_value ?? '',
        [t('tenders.columns.winner', 'Winner')]: r.winner_name ?? '',
        [t('tenders.columns.awarded', 'Awarded')]: r.awarded_value ?? '',
        [t('tenders.columns.cpv', 'CPV')]: (r.cpv_codes ?? []).join(', '),
        [t('tenders.columns.deadline', 'Deadline')]: r.submission_deadline ?? '',
        [t('tenders.columns.portal', 'Portal')]: r.external_reference_url ?? '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Tenders');
      // Local-time timestamp, e.g. tenders_2026-06-01_14-30-22.xlsx
      const d = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const ts = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
      XLSX.writeFile(wb, `tenders_${ts}.xlsx`);
    } catch {
      // silently ignore export errors
    } finally {
      setIsExporting(false);
    }
  };

  // Only seed React Query with the SSR payload when the live query key matches
  // the state the server prefetched (page 0, default sort, no filters). Once
  // the user paginates, sorts, or filters, the key changes and React Query
  // fetches normally.
  const isInitialQuery =
    pagination.pageIndex === 0 &&
    sorting.length === 1 &&
    sorting[0].id === 'publication_date' &&
    sorting[0].desc === true &&
    !searchQuery &&
    selectedContractType === 'all' &&
    selectedStatus === 'all' &&
    selectedBundesland === 'all' &&
    selectedValueRange === 'all' &&
    !selectedCpvCode;

  const { data, isLoading } = useQuery({
    queryKey: [
      'admin-tenders',
      pagination,
      sorting,
      searchQuery,
      selectedContractType,
      selectedStatus,
      selectedBundesland,
      selectedValueRange,
      selectedCpvCode,
    ],
    queryFn: () =>
      fetchTenders({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sorting,
        searchQuery,
      }),
    initialData: isInitialQuery && initialData ? initialData : undefined,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 1,
  });

  const columns = useMemo<ColumnDef<TenderRow>[]>(
    () => [
      {
        accessorKey: 'publication_number',
        id: 'publication_number',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.pubNr', 'Pub. Nr.')} column={column} />
        ),
        cell: ({ row }) => (
          <a
            href={`https://ted.europa.eu/de/notice/-/detail/${row.original.publication_number}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-xs max-w-[120px] truncate text-blue-600 hover:underline dark:text-blue-400"
            title={row.original.publication_number}
          >
            {row.original.publication_number}
          </a>
        ),
        size: 130,
        meta: {
          headerTitle: t('tenders.columns.pubNr', 'Pub. Nr.'),
          skeleton: <Skeleton className="w-24 h-7" />,
        },
        enableSorting: true,
        enableHiding: false,
      },
      {
        accessorKey: 'publication_date',
        id: 'publication_date',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.date', 'Date')} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-sm whitespace-nowrap">
            {formatDate(row.original.publication_date)}
          </div>
        ),
        size: 100,
        meta: { headerTitle: t('tenders.columns.date', 'Date'), skeleton: <Skeleton className="w-20 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'title',
        id: 'title',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.title', 'Title')} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-sm max-w-[300px] truncate" title={row.original.title ?? ''}>
            {row.original.title || '\u2014'}
          </div>
        ),
        size: 300,
        meta: { headerTitle: t('tenders.columns.title', 'Title'), skeleton: <Skeleton className="w-48 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'buyer_name',
        id: 'buyer_name',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.buyer', 'Buyer')} column={column} />
        ),
        cell: ({ row }) => {
          const name = row.original.buyer_name;
          const euId = row.original.buyer_eu_id;
          if (!name) return <span className="text-muted-foreground">{'\u2014'}</span>;
          const slug = tryBuildCompanySlugFromCanonical(
            {
              canonical_name: row.original.buyer_canonical_name,
              canonical_sitz: row.original.buyer_canonical_sitz,
              canonical_register_court: row.original.buyer_canonical_register_court,
              canonical_register_type: row.original.buyer_canonical_register_type,
              canonical_register_id: row.original.buyer_canonical_register_id,
            },
            locale,
          );
          if (slug) {
            return (
              <a
                href={slug}
                className="text-sm max-w-[200px] truncate text-blue-600 hover:underline dark:text-blue-400 block"
                title={`${name} (${euId ?? ''})`}
              >
                {name}
              </a>
            );
          }
          return (
            <div className="text-sm max-w-[200px] truncate" title={name}>{name}</div>
          );
        },
        size: 200,
        meta: { headerTitle: t('tenders.columns.buyer', 'Buyer'), skeleton: <Skeleton className="w-32 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'buyer_town',
        id: 'buyer_town',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.town', 'Town')} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-sm">{row.original.buyer_town || '\u2014'}</div>
        ),
        size: 120,
        meta: { headerTitle: t('tenders.columns.town', 'Town'), skeleton: <Skeleton className="w-20 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'contract_type',
        id: 'contract_type',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.type', 'Type')} column={column} />
        ),
        cell: ({ row }) => {
          const ct = row.original.contract_type;
          if (!ct) return <span className="text-muted-foreground">{'\u2014'}</span>;
          const variant = contractTypeBadge[ct] as 'destructive' | 'warning' | 'info' | undefined;
          return (
            <Badge variant={variant ?? 'secondary'} appearance="ghost">
              {ct}
            </Badge>
          );
        },
        size: 100,
        meta: { headerTitle: t('tenders.columns.type', 'Type'), skeleton: <Skeleton className="w-16 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'estimated_value',
        id: 'estimated_value',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.estValue', 'Est. Value')} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-sm text-right tabular-nums whitespace-nowrap">
            {formatCurrency(row.original.estimated_value, row.original.value_currency)}
          </div>
        ),
        size: 130,
        meta: { headerTitle: t('tenders.columns.estValue', 'Est. Value'), skeleton: <Skeleton className="w-24 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'winner_name',
        id: 'winner_name',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.winner', 'Winner')} column={column} />
        ),
        cell: ({ row }) => {
          const name = row.original.winner_name;
          const euId = row.original.winner_eu_id;
          if (!name) return <span className="text-muted-foreground">{'\u2014'}</span>;
          const slug = tryBuildCompanySlugFromCanonical(
            {
              canonical_name: row.original.winner_canonical_name,
              canonical_sitz: row.original.winner_canonical_sitz,
              canonical_register_court: row.original.winner_canonical_register_court,
              canonical_register_type: row.original.winner_canonical_register_type,
              canonical_register_id: row.original.winner_canonical_register_id,
            },
            locale,
          );
          if (slug) {
            return (
              <a
                href={slug}
                className="text-sm max-w-[200px] truncate text-blue-600 hover:underline dark:text-blue-400 block"
                title={`${name} (${euId ?? ''})`}
              >
                {name}
              </a>
            );
          }
          return (
            <div className="text-sm max-w-[200px] truncate" title={name}>{name}</div>
          );
        },
        size: 200,
        meta: { headerTitle: t('tenders.columns.winner', 'Winner'), skeleton: <Skeleton className="w-32 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'awarded_value',
        id: 'awarded_value',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.awarded', 'Awarded')} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-sm text-right tabular-nums whitespace-nowrap">
            {formatCurrency(row.original.awarded_value, row.original.awarded_currency)}
          </div>
        ),
        size: 130,
        meta: { headerTitle: t('tenders.columns.awarded', 'Awarded'), skeleton: <Skeleton className="w-24 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'lowest_tender_value',
        id: 'lowest_tender_value',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.lowestBid', 'Lowest Bid')} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-sm text-right tabular-nums whitespace-nowrap">
            {formatCurrency(row.original.lowest_tender_value, row.original.awarded_currency)}
          </div>
        ),
        size: 120,
        meta: { headerTitle: t('tenders.columns.lowestBid', 'Lowest Bid'), skeleton: <Skeleton className="w-20 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'highest_tender_value',
        id: 'highest_tender_value',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.highestBid', 'Highest Bid')} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-sm text-right tabular-nums whitespace-nowrap">
            {formatCurrency(row.original.highest_tender_value, row.original.awarded_currency)}
          </div>
        ),
        size: 120,
        meta: { headerTitle: t('tenders.columns.highestBid', 'Highest Bid'), skeleton: <Skeleton className="w-20 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'cpv_codes',
        id: 'cpv_codes',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.cpv', 'CPV')} column={column} />
        ),
        cell: ({ row }) => {
          const codes = row.original.cpv_codes;
          const descs = row.original.cpv_descriptions;
          if (!codes?.length) return <span className="text-muted-foreground">{'\u2014'}</span>;
          const label = descs?.[0] ?? codes[0];
          const title = codes.map((c, i) => `${c} — ${descs?.[i] ?? c}`).join('\n');
          return (
            <div className="flex items-center gap-1.5 whitespace-nowrap" title={title}>
              <span className="text-[10px] font-mono text-muted-foreground">{codes[0]}</span>
              <span className="text-[10px] text-muted-foreground truncate">{label}</span>
              {codes.length > 1 && (
                <span className="text-[10px] text-muted-foreground shrink-0">+{codes.length - 1}</span>
              )}
            </div>
          );
        },
        size: 220,
        meta: { headerTitle: t('tenders.columns.cpv', 'CPV'), skeleton: <Skeleton className="w-20 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'document_type',
        id: 'document_type',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.docType', 'Doc Type')} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-xs max-w-[140px] truncate" title={row.original.document_type ?? ''}>
            {row.original.document_type || '\u2014'}
          </div>
        ),
        size: 120,
        meta: { headerTitle: t('tenders.columns.docType', 'Doc Type'), skeleton: <Skeleton className="w-20 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'submission_deadline',
        id: 'submission_deadline',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.deadline', 'Deadline')} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-sm whitespace-nowrap">
            {formatDate(row.original.submission_deadline)}
          </div>
        ),
        size: 100,
        meta: { headerTitle: t('tenders.columns.deadline', 'Deadline'), skeleton: <Skeleton className="w-20 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'external_reference_url',
        id: 'external_reference_url',
        header: () => <div className="text-xs">{t('tenders.columns.portal', 'Portal')}</div>,
        cell: ({ row }) => {
          const url = row.original.external_reference_url;
          if (!url) return <span className="text-muted-foreground">{'\u2014'}</span>;
          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline dark:text-blue-400 text-xs truncate max-w-[120px] block"
              title={url}
            >
              {t('tenders.columns.link', 'Link')}
            </a>
          );
        },
        size: 70,
        meta: { headerTitle: t('tenders.columns.portal', 'Portal'), skeleton: <Skeleton className="w-10 h-7" /> },
        enableSorting: false,
      },
    ],
    [t, locale],
  );

  const table = useReactTable({
    data: data?.data || [],
    columns,
    pageCount: Math.ceil((data?.pagination?.total || 0) / pagination.pageSize),
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  return (
    <div className="relative lg:fixed lg:inset-x-0 lg:top-[var(--header-height,95px)] lg:bottom-[calc(var(--footer-height,60px)+env(safe-area-inset-bottom,0px))] flex flex-col lg:flex-row min-h-0 w-full max-w-full lg:overflow-hidden bg-background">
      {/* Sidebar filters */}
      <aside
        className={cn(
          'shrink-0 bg-background overflow-y-auto',
          'w-full transition-[max-height] duration-300 ease-in-out',
          'lg:overflow-hidden lg:overflow-y-visible lg:h-full lg:w-auto lg:transition-[width,min-width,max-width] lg:duration-300 lg:ease-in-out',
          filtersOpen
            ? 'max-h-[9999px] border-b lg:max-h-none lg:w-[380px] lg:min-w-[380px] lg:max-w-[380px] lg:border-b-0 lg:border-r'
            : 'max-h-0 lg:max-h-none lg:w-0 lg:min-w-0 lg:max-w-0',
        )}
      >
        <div className="flex flex-col lg:h-full w-full lg:w-[380px] lg:min-w-[380px]">
          {/* Header */}
          <div className="p-4 pb-3 border-b">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-base font-semibold">{t('tenders.title', 'TED Tenders')}</h2>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setFiltersOpen(false)}
                aria-label="Close filters"
                className="h-7 w-7 p-0"
              >
                <X size={16} />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {data?.pagination?.total != null
                ? t('tenders.results', '{{count}} results', { count: data.pagination.total.toLocaleString('de-DE') })
                : '\u00A0'}
            </p>
          </div>

          {/* Scrollable Filters */}
          <div className="flex-1 overflow-y-auto min-h-0 p-4 flex flex-col">
            <div className="space-y-1.5">
              <Label className="text-xs text-foreground font-medium">{t('tenders.search', 'Search')}</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                <Input
                  placeholder={t('tenders.searchPlaceholder', 'Title, buyer, pub nr...')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPagination({ ...pagination, pageIndex: 0 });
                  }}
                  className="h-9 pl-8 placeholder:text-muted-foreground/80"
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setPagination({ ...pagination, pageIndex: 0 });
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                  >
                    <X className="size-3.5 text-muted-foreground" />
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-foreground font-medium">{t('tenders.contractType', 'Contract Type')}</Label>
              <Select
                value={selectedContractType}
                onValueChange={(v) => {
                  setSelectedContractType(v);
                  setPagination({ ...pagination, pageIndex: 0 });
                }}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('tenders.allTypes', 'All Types')}</SelectItem>
                  <SelectItem value="works">{t('tenders.works', 'Works')}</SelectItem>
                  <SelectItem value="supplies">{t('tenders.supplies', 'Supplies')}</SelectItem>
                  <SelectItem value="services">{t('tenders.services', 'Services')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-foreground font-medium">{t('tenders.status', 'Status')}</Label>
              <Select
                value={selectedStatus}
                onValueChange={(v) => {
                  setSelectedStatus(v);
                  setPagination({ ...pagination, pageIndex: 0 });
                }}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('tenders.allStatus', 'All Status')}</SelectItem>
                  <SelectItem value="open">{t('tenders.open', 'Open')}</SelectItem>
                  <SelectItem value="awarded">{t('tenders.awarded', 'Awarded')}</SelectItem>
                  <SelectItem value="closed">{t('tenders.closed', 'Closed')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-foreground font-medium">{t('tenders.valueRange', 'Value Range')}</Label>
              <Select
                value={selectedValueRange}
                onValueChange={(v) => {
                  setSelectedValueRange(v);
                  setPagination({ ...pagination, pageIndex: 0 });
                }}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('tenders.anyValue', 'Any value')}</SelectItem>
                  <SelectItem value="0-100k">&lt; 100k</SelectItem>
                  <SelectItem value="100k-1m">100k &ndash; 1M</SelectItem>
                  <SelectItem value="1m-10m">1M &ndash; 10M</SelectItem>
                  <SelectItem value="10m-50m">10M &ndash; 50M</SelectItem>
                  <SelectItem value="50m+">50M+</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-foreground font-medium">{t('tenders.bundesland', 'Bundesland')}</Label>
              <Select
                value={selectedBundesland}
                onValueChange={(v) => {
                  setSelectedBundesland(v);
                  setPagination({ ...pagination, pageIndex: 0 });
                }}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Bundesl&auml;nder</SelectItem>
                  <SelectItem value="Baden-Württemberg">Baden-W&uuml;rttemberg</SelectItem>
                  <SelectItem value="Bayern">Bayern</SelectItem>
                  <SelectItem value="Berlin">Berlin</SelectItem>
                  <SelectItem value="Brandenburg">Brandenburg</SelectItem>
                  <SelectItem value="Bremen">Bremen</SelectItem>
                  <SelectItem value="Hamburg">Hamburg</SelectItem>
                  <SelectItem value="Hessen">Hessen</SelectItem>
                  <SelectItem value="Mecklenburg-Vorpommern">Mecklenburg-Vorpommern</SelectItem>
                  <SelectItem value="Niedersachsen">Niedersachsen</SelectItem>
                  <SelectItem value="Nordrhein-Westfalen">Nordrhein-Westfalen</SelectItem>
                  <SelectItem value="Rheinland-Pfalz">Rheinland-Pfalz</SelectItem>
                  <SelectItem value="Saarland">Saarland</SelectItem>
                  <SelectItem value="Sachsen">Sachsen</SelectItem>
                  <SelectItem value="Sachsen-Anhalt">Sachsen-Anhalt</SelectItem>
                  <SelectItem value="Schleswig-Holstein">Schleswig-Holstein</SelectItem>
                  <SelectItem value="Thüringen">Th&uuml;ringen</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-foreground font-medium">{t('tenders.cpvCode', 'CPV Code')}</Label>
              <Popover
                open={cpvCodesOpen}
                onOpenChange={(open) => {
                  setCpvCodesOpen(open);
                  if (!open) setCpvSearch('');
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 relative"
                  >
                    <div className="flex flex-wrap gap-1 w-full">
                      {!selectedCpvCode ? (
                        <span className="text-xs text-muted-foreground/80">
                          {t('tenders.cpvPlaceholder', 'e.g. 45000000 Construction work')}
                        </span>
                      ) : (
                        <Badge
                          variant="secondary"
                          className="text-xs font-normal gap-1 pr-1 border border-foreground"
                        >
                          <span className="font-mono text-muted-foreground">
                            {selectedCpvCode}
                          </span>
                          <span className="max-w-[120px] truncate">
                            {cpvCodesList.find((c) => c.code === selectedCpvCode)?.description || ''}
                          </span>
                          <X
                            size={12}
                            className="cursor-pointer hover:bg-muted rounded-sm"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setSelectedCpvCode('');
                              setPagination({ ...pagination, pageIndex: 0 });
                            }}
                          />
                        </Badge>
                      )}
                      <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                >
                  <Command shouldFilter={false}>
                    <CommandInput
                      className="placeholder:text-muted-foreground/80"
                      placeholder={t('tenders.cpvSearch', 'Search CPV code (min. 2 digits)...')}
                      value={cpvSearch}
                      onValueChange={(v) => {
                        setCpvSearch(v);
                        if (cpvListRef.current)
                          cpvListRef.current.scrollTop = 0;
                      }}
                    />
                    <CommandList
                      ref={cpvListRef}
                      className="h-[272px] overflow-y-auto"
                    >
                      <CommandEmpty>
                        {t('tenders.cpvEmpty', 'No CPV code found.')}
                      </CommandEmpty>
                      <CommandGroup>
                        {cpvCodesList.map((cpv) => (
                          <CommandItem
                            key={cpv.code}
                            value={`${cpv.code} ${cpv.description}`}
                            onSelect={() => {
                              setSelectedCpvCode(cpv.code);
                              setCpvCodesOpen(false);
                              setCpvSearch('');
                              setPagination({ ...pagination, pageIndex: 0 });
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedCpvCode === cpv.code
                                  ? 'opacity-100'
                                  : 'opacity-0',
                              )}
                            />
                            <span className="font-mono text-xs text-muted-foreground mr-1.5">
                              {cpv.code}
                            </span>
                            <span className="truncate text-xs">
                              {cpv.description}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:flex-1 min-w-0 flex flex-col relative lg:overflow-hidden">
        {/* Toolbar — mobile-only controls; hidden on desktop to remove the
            empty strip above the data grid. */}
        <div className="lg:hidden flex items-center gap-2 px-3 py-2 border-b bg-background shrink-0">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
            <Input
              placeholder={t('tenders.searchTenders', 'Search tenders...')}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPagination({ ...pagination, pageIndex: 0 });
              }}
              className="h-8 pl-8 text-xs"
            />
          </div>
          <Select
            value={selectedContractType}
            onValueChange={(v) => {
              setSelectedContractType(v);
              setPagination({ ...pagination, pageIndex: 0 });
            }}
          >
            <SelectTrigger className="w-[120px] h-8 text-xs lg:hidden">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('tenders.allTypes', 'All Types')}</SelectItem>
              <SelectItem value="works">{t('tenders.works', 'Works')}</SelectItem>
              <SelectItem value="supplies">{t('tenders.supplies', 'Supplies')}</SelectItem>
              <SelectItem value="services">{t('tenders.services', 'Services')}</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={selectedStatus}
            onValueChange={(v) => {
              setSelectedStatus(v);
              setPagination({ ...pagination, pageIndex: 0 });
            }}
          >
            <SelectTrigger className="w-[110px] h-8 text-xs lg:hidden">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('tenders.allStatus', 'All')}</SelectItem>
              <SelectItem value="open">{t('tenders.open', 'Open')}</SelectItem>
              <SelectItem value="awarded">{t('tenders.awarded', 'Awarded')}</SelectItem>
              <SelectItem value="closed">{t('tenders.closed', 'Closed')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Desktop toolbar — Spalten (column visibility) + Excel export, same
            as the /search results toolbar. Authenticated users only; guests see
            the sign-in overlay instead. */}
        {!isGuest && (
          <div className="hidden lg:flex items-center justify-end gap-1.5 px-3 py-2 border-b bg-background shrink-0">
            <DataGridColumnVisibility
              table={table}
              trigger={
                <Button
                  variant="outline"
                  size="sm"
                  className="h-auto min-h-7 py-1 text-[11px] gap-1 rounded-full"
                >
                  <SlidersHorizontal className="h-3 w-3 shrink-0" />
                  {t('search.columns', 'Spalten')}
                </Button>
              }
            />
            <Button
              variant="outline"
              size="sm"
              className="h-auto min-h-7 py-1 text-[11px] gap-1 rounded-full"
              onClick={handleExport}
              disabled={isExporting || !data?.data?.length}
            >
              <Download className="h-3 w-3 shrink-0" />
              {isExporting
                ? t('search.exporting', 'Exportiere…')
                : t('search.exportExcel', 'Excel Export')}
            </Button>
          </div>
        )}

        <div className={cn(
          'relative flex-1 min-h-0 flex flex-col',
          isGuest && '[&_tbody_tr:nth-child(n+6)]:blur-sm [&_tbody_tr:nth-child(n+6)]:select-none [&_tbody_tr:nth-child(n+6)]:pointer-events-none',
        )}>
          <DataGrid
            table={table}
            recordCount={data?.pagination?.total || 0}
            isLoading={isLoading}
            tableLayout={{
              columnsResizable: true,
              columnsPinnable: true,
              columnsMovable: true,
              columnsVisibility: true,
            }}
            tableClassNames={{ edgeCell: 'px-3' }}
          >
            <Card className="border-0 rounded-none shadow-none flex flex-col flex-1 min-h-0">
              <CardTable className="flex-1 min-h-0">
                <ScrollArea className="h-full w-full">
                  <DataGridTable />
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
              </CardTable>
              {!isGuest && (
                <CardFooter className="border-t shrink-0">
                  <DataGridPagination />
                </CardFooter>
              )}
            </Card>
          </DataGrid>
          {isGuest && (
            <div className="absolute inset-x-0 bottom-0 top-[200px] flex items-center justify-center z-10">
              <div
                className="flex flex-col items-center justify-center p-8 text-center"
                data-nosnippet
              >
                <div className="flex flex-col items-center gap-4">
                  <div className="space-y-1 text-center">
                    <h3 className="text-lg font-semibold">{t('tenders.euTenders', 'EU Tenders')}</h3>
                    <p className="text-sm text-muted-foreground max-w-[240px] mx-auto">
                      {t('tenders.signInToView', 'Please sign in to view this content.')}
                    </p>
                  </div>
                  <Button className="mt-2" onClick={() => onSignIn?.()}>
                    {t('tenders.signIn', 'Sign in to view')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Pro-feature prompt shown when a non-entitled user clicks Export. */}
      <Dialog open={showProDialog} onOpenChange={setShowProDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('search.proFeature.title', 'Pro-Funktion')}</DialogTitle>
            <DialogDescription>
              {t(
                'search.proFeature.description',
                'Der Excel-Export ist nur für Pro-Nutzer verfügbar. Mit einem Pro-Konto können Sie Suchergebnisse exportieren.',
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProDialog(false)}>
              {t('common.buttons.close', 'Schließen')}
            </Button>
            <Button asChild>
              <LocaleLink href="/plans" onClick={() => setShowProDialog(false)}>
                {t('search.proFeature.cta', 'Pläne ansehen')}
              </LocaleLink>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TendersList;
